import { SubscriptionPurchaseBackdrop } from "@/components/subscription/subscription-purchase-backdrop";
import { useMonthlyTrialEligibility } from "@/features/paywall/hooks/use-monthly-trial-eligibility";
import { usePurchaseAndVerify } from "@/features/paywall/hooks/use-purchase-and-verify";
import { useSubscriptionOfferings } from "@/features/settings/hooks/use-subscription-products";
import { isPaywallOpenAtom } from "@/stores/paywall.store";
import { getTrialPeriodLabelWithFallback } from "@/utils/revenue-cat/trial-period";
import { useIonRouter } from "@ionic/react";
import { PACKAGE_TYPE, type PurchasesPackage } from "@revenuecat/purchases-capacitor";
import { useSetAtom } from "jotai";
import ExpiredTrialPaywall from "../expired-trial-paywall";
import { FreeTrialOfferView } from "./free-trial-offer-view";
import { usePaywallViewed } from "../../analytics";

const FALLBACK_MONTHLY_PRICE = "£9.99";

/**
 * The credit-card-committed free-trial offer. Purchases the monthly package
 * (which carries the store free-trial offer) via RevenueCat.
 */
function FreeTrialOffer({ monthlyPackage }: { monthlyPackage: PurchasesPackage | undefined }) {
  usePaywallViewed("trial_offer");
  const router = useIonRouter();
  const setIsPaywallOpen = useSetAtom(isPaywallOpenAtom);

  const monthlyPrice = monthlyPackage?.product.priceString ?? FALLBACK_MONTHLY_PRICE;
  const trialLabel = getTrialPeriodLabelWithFallback(monthlyPackage);

  const {
    isPurchasing,
    isBackdropOpen,
    backdropState,
    startPurchase,
    handleManualRetry,
    handleBackdropCancel,
  } = usePurchaseAndVerify({
    placement: "trial_offer",
    logPrefix: "[Paywall]",
    onUnlocked: () => {
      setIsPaywallOpen(false);
      router.push("/overview", "none", "replace");
    },
  });

  return (
    <FreeTrialOfferView
      monthlyPrice={monthlyPrice}
      trialLabel={trialLabel}
      isBusy={isPurchasing}
      canStart={!!monthlyPackage}
      onStart={() => startPurchase(monthlyPackage)}
      backdrop={
        <SubscriptionPurchaseBackdrop
          isOpen={isBackdropOpen}
          state={backdropState}
          onRetry={handleManualRetry}
          onCancel={handleBackdropCancel}
        />
      }
    />
  );
}

/**
 * Soft paywall with the store-eligibility gate: if the store won't grant a free
 * trial (e.g. this account already subscribed), don't promise one — fall back to
 * the approved "trial has ended" paywall rather than a bespoke screen. "unknown"
 * (still resolving) is treated optimistically as eligible so the eligible path
 * never flashes the pay screen.
 */
export default function FreeTrialPaywall() {
  const { offerings } = useSubscriptionOfferings();

  const monthlyPackage = offerings?.current?.availablePackages?.find(
    (p) => p.packageType === PACKAGE_TYPE.MONTHLY,
  );

  const trialEligibility = useMonthlyTrialEligibility(monthlyPackage);
  if (trialEligibility === "ineligible") {
    return <ExpiredTrialPaywall />;
  }

  return <FreeTrialOffer monthlyPackage={monthlyPackage} />;
}
